import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  X, 
  Check, 
  ArrowRight,
  Phone,
  Mail,
  FileText,
  Clock,
  Users,
  Zap,
  TrendingUp,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const beforeItems = [
  { icon: Phone, text: 'Téléphone qui sonne en permanence' },
  { icon: Mail, text: 'Emails qui s\'accumulent' },
  { icon: FileText, text: 'Devis envoyés en retard' },
  { icon: Clock, text: 'Peu de temps pour chercher de nouveaux clients' },
  { icon: Users, text: 'Sentiment d\'être seul pour tout gérer' },
  { icon: Zap, text: 'Surcharge mentale permanente' },
];

const afterItems = [
  { icon: Users, text: 'Une assistante à mi-temps ou plein temps qui organise l\'activité' },
  { icon: Mail, text: 'Emails et demandes clients traités rapidement' },
  { icon: Phone, text: 'Standard téléphonique intelligent qui répond aux appels' },
  { icon: Zap, text: 'Organisation administrative structurée' },
  { icon: TrendingUp, text: 'Visibilité améliorée sur internet' },
  { icon: Heart, text: 'Un entrepreneur concentré sur son expertise' },
];

export default function TransformationPage() {
  return (
    <div data-testid="transformation-page">
      {/* Hero */}
      <section className="py-20 md:py-32 gradient-mesh">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-rm-text mb-6">
              Et si ton entreprise fonctionnait{' '}
              <span className="text-gradient">enfin sans toi ?</span>
            </h1>
            <p className="text-lg md:text-xl text-rm-text-secondary mb-8">
              BatiAssist permet aux entrepreneurs débordés de transformer leur organisation 
              grâce à un assistant à mi-temps ou plein temps et une équipe digitale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none text-rm-text-secondary"
          >
            <p className="text-lg leading-relaxed">
              Beaucoup d'entrepreneurs créent leur entreprise pour être libres. Mais avec le temps, 
              ils se retrouvent souvent seuls à gérer : les appels, les emails, les devis, les clients, 
              l'administratif, la recherche de nouveaux chantiers.
            </p>
            <p className="text-lg leading-relaxed">
              Ils passent leurs journées à courir entre les urgences, les chantiers et les demandes clients. 
              BatiAssist a été créé pour changer cette situation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-rm-text mb-6">
              La transformation
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Before */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-8 border-l-4 border-red-400"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <X className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-rm-text">Avant BatiAssist</h3>
                  <p className="text-red-600">Un entrepreneur seul qui doit tout gérer</p>
                </div>
              </div>
              <ul className="space-y-4">
                {beforeItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <li key={index} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-rm-text-secondary">{item.text}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-6 p-4 bg-red-100/50 rounded-xl">
                <p className="text-red-700 text-sm font-medium">
                  L'entrepreneur passe son temps à gérer l'urgence au lieu de développer son activité.
                </p>
              </div>
            </motion.div>

            {/* After */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 border-l-4 border-green-400"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-rm-text">Après BatiAssist</h3>
                  <p className="text-green-600">Une entreprise organisée</p>
                </div>
              </div>
              <ul className="space-y-4">
                {afterItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <li key={index} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-rm-text-secondary">{item.text}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-6 p-4 bg-green-100/50 rounded-xl">
                <p className="text-green-700 text-sm font-medium">
                  L'entrepreneur retrouve du temps pour son métier, ses clients et sa vie personnelle.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* New Organization */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-rm-text mb-6">
              Tu ne travailles plus seul
            </h2>
            <p className="text-lg md:text-xl text-rm-text-secondary mb-8 leading-relaxed">
              Avec BatiAssist, tu passes d'un entrepreneur seul à une entreprise organisée. 
              Ton organisation comprend un assistant à mi-temps ou plein temps, une équipe digitale composée 
              d'assistants spécialisés et des outils automatisés pour organiser ton activité.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projection */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-rm-accent/10">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-rm-text mb-6">
              Et si ton entreprise fonctionnait différemment ?
            </h2>
            <p className="text-lg md:text-xl text-rm-text-secondary mb-8 leading-relaxed">
              Imagine une entreprise où les appels sont gérés, les emails sont traités, 
              les demandes clients sont suivies, l'organisation est structurée et les opportunités 
              commerciales sont mieux identifiées. Tu peux alors te concentrer sur ce que tu fais le mieux.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group">
                Réserver un entretien avec Romain
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
